import { type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  IconCategory2,
  IconLayoutSidebar,
  IconLogout,
  IconPlus,
  IconPresentationAnalytics,
  IconSearch,
  IconSelector,
  IconSettings,
  IconTemplate,
  IconUserCircle,
  IconWorld,
} from "@tabler/icons-react";
import { cn } from "../../../lib/utils";
import ProfilePicture, { type ProfilePictureUser } from "../../ProfilePicture";
import { Badge } from "../../ui/Badge";
import { Logo } from "../../ui/Logo";
import { Tooltip } from "../../ui/Tooltip";
import { buttonVariants } from "../../ui/Button";
import { usePopoverMenu } from "../../ui/Popover";
import {
  DesktopMenu,
  DesktopMenuDivider,
  DesktopMenuItem,
  DesktopMenuRadioItem,
  DesktopSubmenu,
} from "../../ui/DesktopMenu";
import type { SupportedLanguage } from "../../../interfaces";
import {
  LANGUAGE_LABELS,
  LANGUAGE_ORDER,
} from "../../settings/sheet/views/LanguageView";
import { useDragResize } from "../../../hooks/use-drag-resize";
import Skeleton from "../../skeleton/Skeleton";

/** The destinations the sidebar links to, besides JuisciGPT itself. */
export type DesktopSidebarDestination =
  "explore" | "rooms" | "visual-abstracts";

export type DesktopSidebarState = "expanded" | "collapsed";

/**
 * Bounds of the expanded width, in CSS pixels. The rail is always 64.
 *
 * The default is the Figma desktop grid's 240, the width every page's column
 * math assumes: at the 1280 minimum window it leaves the 960px content block
 * its 40px margins. A wider sidebar is the reader's choice, not the default.
 */
export const DESKTOP_SIDEBAR_WIDTH = {
  min: 240,
  default: 240,
  max: 400,
} as const;

// One arrow-key press on the resize handle.
const KEYBOARD_RESIZE_STEP = 16;

export type DesktopSidebarRecents =
  | { status: "pending" }
  | { status: "error" }
  | {
      status: "success";
      // `menu` is the row's owner actions (Share, Rename, Delete), built by
      // the caller that owns the writes; the row reveals it on hover.
      conversations: ReadonlyArray<{
        id: string;
        title: string;
        menu: React.ReactNode;
      }>;
      /** Whether these rows answer a search (picks the empty-state copy). */
      searched: boolean;
      /** Null when every page is loaded. */
      onLoadMore: (() => void) | null;
    };

export type DesktopSidebarUser = {
  name: string;
  email: string;
  /** The translated main specialty, under the name. Null when none is set. */
  specialty: string | null;
  /** The app language, which the account menu's Language submenu checks. */
  language: SupportedLanguage;
  picture: ProfilePictureUser;
};

/**
 * Who the sidebar is for. `pending` is the cold boot (Figma "DesktopSidebar,
 * Loading=True"): the signed-in user is not known yet, so everything that
 * belongs to them (the search, the recents, the "New" badges, the account)
 * is a skeleton, while the logo and the destinations, which belong to nobody,
 * are already the real ones.
 */
export type DesktopSidebarSession =
  | { status: "pending" }
  | {
      status: "ready";
      /** "New conversation" was clicked, before its navigation. */
      onNewConversation: () => void;
      activeConversationId: string | null;
      /** Shows the "New" badge (a dot on the collapsed rail). */
      newContent: Record<DesktopSidebarDestination, boolean>;
      search: string;
      onSearchChange: (value: string) => void;
      recents: DesktopSidebarRecents;
      user: DesktopSidebarUser;
      onOpenSettings: () => void;
      onChangeLanguage: (language: SupportedLanguage) => void;
      onLogOut: () => void;
    };

// The rows, in order. Rooms opens the room center.
const DESTINATIONS = [
  "explore",
  "rooms",
  "visual-abstracts",
] as const satisfies ReadonlyArray<DesktopSidebarDestination>;

const DESTINATION_PATHS = {
  explore: "/discovery",
  rooms: "/rooms",
  "visual-abstracts": "/visual-abstracts",
} as const satisfies Record<DesktopSidebarDestination, string>;

const DESTINATION_ICONS = {
  explore: IconTemplate,
  rooms: IconCategory2,
  "visual-abstracts": IconPresentationAnalytics,
} as const satisfies Record<DesktopSidebarDestination, typeof IconTemplate>;

// Loading rows of the recents list. Uneven on purpose: titles are one
// truncated line of prose, and identical bars read as a table.
const RECENTS_SKELETON_WIDTHS = ["72%", "88%", "55%", "80%"] as const;

const rowClass =
  "flex h-8.5 w-full items-center gap-3 rounded-lg px-2.5 font-dm-sans text-ui-body font-medium transition-colors duration-150";

// Recent conversations have no icon and a lighter, smaller title.
const recentRowClass =
  "flex h-8.5 w-full items-center rounded-lg px-2.5 font-dm-sans text-sm font-normal transition-colors duration-150";

const searchInputClass =
  "min-w-0 flex-1 border-none bg-transparent font-dm-sans text-footnote font-medium text-ds-slate-900 outline-none placeholder:text-ds-slate-600";

// Nothing is new before the session: the badges are the user's.
function hasNewContent(
  session: DesktopSidebarSession,
  destination: DesktopSidebarDestination
): boolean {
  return session.status === "ready" && session.newContent[destination];
}

function rowStateClass(active: boolean): string {
  return active
    ? "bg-ds-moss-100 text-ds-mint-800"
    : "text-ds-slate-900 hover:bg-ds-black/4";
}

// Icons sit a step lighter than their label, except on the active row.
function rowIconClass(active: boolean): string {
  return active ? "size-5 shrink-0" : "size-5 shrink-0 text-ds-slate-700";
}

/**
 * Names an icon-only control of the sidebar on hover or focus (Figma
 * "Tooltip/Type=Label", the rail's "Expand sidebar"), in place of the
 * browser's own `title` bubble.
 */
function RailLabel({
  text,
  children,
}: {
  text: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <Tooltip
      trigger={{
        kind: "box",
        className: "inline-flex",
        control: () => children,
      }}
      side="below"
      content={{ kind: "label", text }}
    />
  );
}

/**
 * The desktop navigation, and the only one: JuisciGPT (the sidebar itself),
 * a new conversation, the conversation search, the destinations (Explore,
 * Rooms, Visual abstracts), then the recent conversations. Collapsed, it is a rail of icons.
 *
 * Presentational: the caller owns the collapse state, the search text, the data
 * and the active route, so every state can be pinned in Storybook.
 */
export function DesktopSidebar({
  state,
  onToggle,
  width,
  onWidthChange,
  activeDestination,
  session,
  audioPlayer,
}: {
  state: DesktopSidebarState;
  onToggle: () => void;
  /** Expanded width, within `DESKTOP_SIDEBAR_WIDTH`. Kept while collapsed. */
  width: number;
  /**
   * Called once a resize settles (drag released, double-click, arrow key), with
   * a width already clamped to `DESKTOP_SIDEBAR_WIDTH`. The drag itself is
   * previewed locally, so a caller that persists the width writes it once.
   */
  onWidthChange: (width: number) => void;
  /** Null on JuisciGPT and on any page outside the destinations. */
  activeDestination: DesktopSidebarDestination | null;
  session: DesktopSidebarSession;
  /** The audio player above the footer, when something plays. */
  audioPlayer: ReactNode;
}): React.JSX.Element {
  const { t } = useTranslation();
  const labels: Record<DesktopSidebarDestination, string> = {
    explore: t("Explore"),
    rooms: t("Rooms"),
    "visual-abstracts": t("Visual abstracts"),
  };
  const toggleLabel =
    state === "expanded" ? t("Collapse sidebar") : t("Expand sidebar");
  // The right edge: drag, double-click back to the default, arrow keys.
  const resize = useDragResize({
    value: width,
    min: DESKTOP_SIDEBAR_WIDTH.min,
    max: DESKTOP_SIDEBAR_WIDTH.max,
    step: KEYBOARD_RESIZE_STEP,
    reset: DESKTOP_SIDEBAR_WIDTH.default,
    onCommit: onWidthChange,
  });

  // Both branches keep the right padding one step short of the left: the 1px
  // right border sits inside the box, and the column has to keep the Figma
  // width (36px on the rail, exactly one button, which a full padding would
  // push off-centre).
  if (state === "collapsed") {
    return (
      <aside className="flex h-screen w-16 shrink-0 flex-col items-center border-r border-ds-slate-100 bg-ds-black/2 pl-3.5 pr-3.25 pt-3">
        <div className="flex h-12 items-center">
          <RailLabel text={toggleLabel}>
            <button
              type="button"
              onClick={onToggle}
              aria-label={toggleLabel}
              className="flex size-9 cursor-pointer items-center justify-center rounded-lg text-ds-slate-700 hover:bg-ds-black/4"
            >
              <IconLayoutSidebar className="size-4" stroke={1.25} />
            </button>
          </RailLabel>
        </div>
        <nav className="flex flex-col items-center gap-2 py-2">
          <RailLabel text={t("New conversation")}>
            <Link
              to="/gpt/chat/new"
              onClick={
                session.status === "ready"
                  ? session.onNewConversation
                  : undefined
              }
              aria-label={t("New conversation")}
              className={cn(
                buttonVariants({
                  variant: "secondary",
                  size: "sm",
                  mode: "iconOnly",
                }),
                "w-9"
              )}
            >
              <IconPlus stroke={1.25} />
            </Link>
          </RailLabel>
          {/* Searching needs the list, so the rail opens the sidebar. */}
          <RailLabel text={t("Search a conversation")}>
            <button
              type="button"
              onClick={onToggle}
              aria-label={t("Search a conversation")}
              className="flex size-9 cursor-pointer items-center justify-center rounded-lg bg-ds-black/4 text-ds-slate-600"
            >
              <IconSearch className="size-4" stroke={1.25} />
            </button>
          </RailLabel>
          <div className="flex flex-col items-center gap-0.5 pt-1">
            {DESTINATIONS.map((destination) => {
              const Icon = DESTINATION_ICONS[destination];
              return (
                <RailLabel key={destination} text={labels[destination]}>
                  <Link
                    to={DESTINATION_PATHS[destination]}
                    aria-label={labels[destination]}
                    className={cn(
                      "flex h-8.5 w-9 items-center justify-center rounded-lg transition-colors duration-150",
                      rowStateClass(activeDestination === destination)
                    )}
                  >
                    <Badge
                      show={hasNewContent(session, destination)}
                      position="br"
                    >
                      <Icon
                        className={rowIconClass(
                          activeDestination === destination
                        )}
                        stroke={1.5}
                      />
                    </Badge>
                  </Link>
                </RailLabel>
              );
            })}
          </div>
        </nav>
        <div className="flex-1" />
        <div className="shrink-0 pb-3 empty:hidden">{audioPlayer}</div>
        <div className="-mr-3.25 -ml-3.5 shrink-0 self-stretch border-t border-ds-slate-100 pr-3.25 pl-3.5 py-2">
          {session.status === "ready" ? (
            <UserMenu
              user={session.user}
              onOpenSettings={session.onOpenSettings}
              onChangeLanguage={session.onChangeLanguage}
              onLogOut={session.onLogOut}
            >
              <UserAvatar user={session.user} />
            </UserMenu>
          ) : (
            // The rail's column is 36 wide: no side padding around the 32.
            <div className="flex justify-center py-2">
              <Skeleton
                $variant="circular"
                className="shrink-0"
                width={32}
                height={32}
              />
            </div>
          )}
        </div>
      </aside>
    );
  }

  return (
    <aside
      // A dragged value, not a design token: the bounds are the tokens.
      style={{ width: resize.shownValue }}
      className="relative flex h-screen shrink-0 flex-col border-r border-ds-slate-100 bg-ds-black/2 pl-4 pr-3.75 pt-3"
    >
      <Tooltip
        trigger={{
          kind: "box",
          className: "absolute inset-y-0 -right-1 z-10 flex w-2",
          control: (descriptionId) => (
            <div
              {...resize.separatorProps}
              aria-label={t("Resize sidebar")}
              aria-describedby={descriptionId}
              className="group flex w-full cursor-col-resize justify-center outline-none"
            >
              <span
                className={cn(
                  "h-full w-0.5 transition-colors duration-150 group-hover:bg-ds-slate-200 group-focus-visible:bg-ds-mint-600",
                  resize.isDragging ? "bg-ds-mint-600" : "bg-transparent"
                )}
              />
            </div>
          ),
        }}
        side="end"
        content={{
          kind: "card",
          title: t("Resize sidebar"),
          body: t("Drag to resize. Double-click to restore the default width."),
        }}
      />
      <div className="flex h-12 shrink-0 items-center justify-between gap-2 pl-0.5">
        <Logo variant="no-tagline" tone="color" className="h-6" />
        <RailLabel text={toggleLabel}>
          <button
            type="button"
            onClick={onToggle}
            aria-label={toggleLabel}
            className="flex size-8 cursor-pointer items-center justify-center rounded-lg text-ds-slate-700 hover:bg-ds-black/4"
          >
            <IconLayoutSidebar className="size-4" stroke={1.25} />
          </button>
        </RailLabel>
      </div>

      <nav className="flex shrink-0 flex-col gap-2 py-2">
        <Link
          to="/gpt/chat/new"
          onClick={
            session.status === "ready" ? session.onNewConversation : undefined
          }
          className={cn(
            buttonVariants({
              variant: "secondary",
              size: "sm",
              mode: "default",
            }),
            "w-full shrink-0 text-footnote font-medium no-underline"
          )}
        >
          {t("New conversation")}
        </Link>

        <label className="flex h-9 shrink-0 items-center gap-2 rounded-lg bg-ds-black/4 px-2.5 text-ds-slate-600">
          <IconSearch className="size-4 shrink-0" stroke={1.25} />
          {/* There is nothing to search before the session: the field shows,
              but takes no text. */}
          {session.status === "ready" ? (
            <input
              type="search"
              value={session.search}
              onChange={(event) => {
                session.onSearchChange(event.target.value);
              }}
              placeholder={t("Search a conversation")}
              className={searchInputClass}
            />
          ) : (
            <input
              type="search"
              disabled
              placeholder={t("Search a conversation")}
              className={searchInputClass}
            />
          )}
        </label>

        <div className="flex flex-col gap-0.5 pt-1">
          {DESTINATIONS.map((destination) => {
            const Icon = DESTINATION_ICONS[destination];
            return (
              <Link
                key={destination}
                to={DESTINATION_PATHS[destination]}
                className={cn(
                  rowClass,
                  rowStateClass(activeDestination === destination)
                )}
              >
                <Icon
                  className={rowIconClass(activeDestination === destination)}
                  stroke={1.5}
                />
                <span className="flex-1 truncate">{labels[destination]}</span>
                {hasNewContent(session, destination) ? <NewPill /> : null}
              </Link>
            );
          })}
        </div>
      </nav>

      <div className="flex min-h-0 flex-1 flex-col pt-2">
        <h3 className="m-0 shrink-0 pt-3 pr-2 pb-1 font-dm-sans text-caption font-bold uppercase text-ds-slate-500">
          {t("Recent")}
        </h3>
        {/* Before the session the list is simply not loaded yet. */}
        <RecentList
          recents={
            session.status === "ready" ? session.recents : { status: "pending" }
          }
          activeConversationId={
            session.status === "ready" ? session.activeConversationId : null
          }
        />
      </div>

      <div className="shrink-0 py-3 empty:hidden">{audioPlayer}</div>

      {/* Fixed under its divider, which spans the whole sidebar: only the
          recents above scroll. */}
      <div className="-mr-3.75 -ml-4 shrink-0 border-t border-ds-slate-100 py-2 pr-1.75 pl-3">
        {session.status === "ready" ? (
          <UserMenu
            user={session.user}
            onOpenSettings={session.onOpenSettings}
            onChangeLanguage={session.onChangeLanguage}
            onLogOut={session.onLogOut}
          >
            <UserAvatar user={session.user} />
            <span className="flex min-w-0 flex-1 flex-col text-left font-dm-sans">
              <span className="truncate text-sm font-semibold text-ds-slate-700">
                {session.user.name}
              </span>
              {session.user.specialty !== null ? (
                <span className="truncate text-desktop-meta text-ds-slate-500">
                  {session.user.specialty}
                </span>
              ) : null}
            </span>
            <IconSelector
              className="size-4 shrink-0 text-ds-slate-500"
              stroke={1.25}
            />
          </UserMenu>
        ) : (
          // The account button's box: the avatar, then the name over the
          // specialty. No menu: there is no account to open yet.
          <div className="flex items-center gap-2.5 p-2">
            <Skeleton $variant="circular" width={32} height={32} />
            <span className="flex flex-col gap-2">
              <Skeleton width={112} height={12} />
              <Skeleton width={72} height={10} />
            </span>
          </div>
        )}
      </div>
    </aside>
  );
}

function NewPill(): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <span className="shrink-0 rounded-pill bg-ds-strawberry-500 px-1.5 py-0.5 font-dm-sans text-caption leading-none font-semibold text-white">
      {t("New")}
    </span>
  );
}

function RecentList({
  recents,
  activeConversationId,
}: {
  recents: DesktopSidebarRecents;
  activeConversationId: string | null;
}): React.JSX.Element {
  const { t } = useTranslation();

  if (recents.status === "pending") {
    return (
      <div className="flex flex-col gap-0.5">
        {RECENTS_SKELETON_WIDTHS.map((width) => (
          <div key={width} className="flex h-8.5 items-center px-2.5">
            <Skeleton $variant="text" width={width} height={20} />
          </div>
        ))}
      </div>
    );
  }

  if (recents.status === "error") {
    return (
      <p className="m-0 px-2 pt-1 font-dm-sans text-footnote font-medium text-ds-strawberry-600">
        {t("Something went wrong")}
      </p>
    );
  }

  if (recents.conversations.length === 0) {
    return (
      <p className="m-0 px-2 pt-1 font-dm-sans text-footnote font-medium text-ds-slate-500">
        {recents.searched
          ? t("No conversation matches your search.")
          : t("No conversations yet")}
      </p>
    );
  }

  return (
    <div className="-mx-1 flex min-h-0 flex-col gap-0.5 overflow-y-auto px-1 pt-1">
      {recents.conversations.map((conversation) => (
        // The menu is a sibling of the link, never inside it: an anchor may
        // not hold a button. The row carries the state and hover colours so
        // the two read as one line.
        <div
          key={conversation.id}
          className={cn(
            "group relative flex shrink-0 items-center rounded-lg",
            rowStateClass(activeConversationId === conversation.id)
          )}
        >
          {/* The menu floats over the end of the row, so a title keeps its
              full width until the row is hovered, focused or has its menu
              open: only then does it make room for the trigger. Without
              hover (a touchscreen) the trigger always shows, and so does
              its room. */}
          <Link
            to={`/gpt/chat/conversation/${conversation.id}`}
            className={cn(
              recentRowClass,
              "min-w-0 flex-1 text-inherit no-underline group-focus-within:pr-10 group-hover:pr-10 group-has-data-[open=true]:pr-10 [@media(hover:none)]:pr-10"
            )}
          >
            <span className="flex-1 truncate">{conversation.title}</span>
          </Link>
          <div className="absolute inset-y-0 right-0.5 flex items-center">
            {conversation.menu}
          </div>
        </div>
      ))}
      {recents.onLoadMore !== null ? (
        <button
          type="button"
          onClick={recents.onLoadMore}
          className="shrink-0 cursor-pointer border-none bg-transparent px-2.5 py-1.5 text-left font-dm-sans text-footnote font-semibold text-ds-slate-500 hover:text-ds-slate-700"
        >
          {t("Show more")}
        </button>
      ) : null}
      <Link
        to="/gpt/history"
        // A row like the titles above, in the subtle tone.
        className={cn(
          recentRowClass,
          "shrink-0 font-medium text-ds-slate-500 no-underline hover:bg-ds-black/4 hover:text-ds-slate-700"
        )}
      >
        {t("See all")}
      </Link>
    </div>
  );
}

function UserAvatar({ user }: { user: DesktopSidebarUser }): React.JSX.Element {
  return <ProfilePicture user={user.picture} height={32} badge={null} />;
}

/**
 * The footer's account menu (Figma "Menu · compte"): who is signed in, the
 * profile, the settings, the language in its submenu (Figma
 * "account-menu.language", applied at the click), then logging out.
 */
function UserMenu({
  user,
  onOpenSettings,
  onChangeLanguage,
  onLogOut,
  children,
}: {
  user: DesktopSidebarUser;
  onOpenSettings: () => void;
  onChangeLanguage: (language: SupportedLanguage) => void;
  onLogOut: () => void;
  children: ReactNode;
}): React.JSX.Element {
  const { t } = useTranslation();
  const menu = usePopoverMenu("right");

  return (
    <>
      <button
        ref={menu.anchor.triggerRef}
        type="button"
        aria-label={user.name}
        aria-haspopup="menu"
        aria-expanded={menu.isOpen}
        onClick={menu.toggle}
        className="flex w-full shrink-0 cursor-pointer items-center justify-center gap-2.5 rounded-lg border-none bg-transparent p-2 hover:bg-ds-black/4"
      >
        {children}
      </button>
      {menu.isOpen ? (
        <DesktopMenu anchor={menu.anchor} className="w-62">
          <div className="flex items-center gap-2.5 p-2.5">
            <UserAvatar user={user} />
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-sm font-semibold text-ds-slate-950">
                {user.name}
              </span>
              <span className="truncate text-xs font-medium text-ds-slate-500">
                {user.email}
              </span>
            </span>
          </div>
          <DesktopMenuDivider />
          <DesktopMenuItem
            description={null}
            icon={IconUserCircle}
            label={t("View profile")}
            trailing={null}
            tone="default"
            action={{ kind: "link", to: "/profile", onClick: menu.close }}
          />
          <DesktopMenuItem
            description={null}
            icon={IconSettings}
            label={t("All settings")}
            trailing={{ kind: "hint", text: "⇧⌘," }}
            tone="default"
            action={{
              kind: "button",
              onClick: () => {
                menu.close();
                onOpenSettings();
              },
            }}
          />
          <DesktopSubmenu
            icon={IconWorld}
            label={t("Language")}
            hint={LANGUAGE_LABELS[user.language].name}
            className="w-66"
          >
            {LANGUAGE_ORDER.map((lang) => (
              <DesktopMenuRadioItem
                key={lang}
                label={LANGUAGE_LABELS[lang].name}
                hint={LANGUAGE_LABELS[lang].native}
                checked={lang === user.language}
                onClick={() => {
                  menu.close();
                  onChangeLanguage(lang);
                }}
              />
            ))}
            <DesktopMenuDivider />
            <p className="m-0 px-2.5 py-1.5 text-desktop-meta text-ds-slate-700">
              {t("Scientific content stays in its original language.")}
            </p>
          </DesktopSubmenu>
          <DesktopMenuDivider />
          <DesktopMenuItem
            description={null}
            icon={IconLogout}
            label={t("Log out")}
            trailing={null}
            tone="default"
            action={{
              kind: "button",
              onClick: () => {
                menu.close();
                onLogOut();
              },
            }}
          />
        </DesktopMenu>
      ) : null}
    </>
  );
}
